import React from 'react';
import clsx from 'clsx';
import {
  PAYWALL_EXTENSION_BADGE_COLORS,
  PAYWALL_PLAN_FEATURES,
} from '@/features/tool/constants/paywall';
import {
  PaywallCheckoutFrame,
  PaywallPdfPage,
  PaywallPriceText,
} from './components';
import Paywall2LegalText from './components/Paywall2LegalText';
import { CircleCheckIcon, DocumentReadyIcon, StepCheckIcon } from './icons';
import type { PaywallCheckoutProps, PaywallDocumentProps } from './types';
import { splitFileName } from './utils';

const PriceText = ({ value }: { value: string }) => (
  <PaywallPriceText
    value={value}
    className="shrink-0 whitespace-nowrap leading-normal text-[#292A33]"
  />
);

const ExtensionBadge = ({
  extension,
  className = '',
}: {
  extension: string;
  className?: string;
}) => (
  <span
    className={clsx(
      'flex h-[30px] shrink-0 items-center justify-center rounded-[4px] border-2 border-white px-2 text-[14px] font-medium uppercase leading-none text-white',
      PAYWALL_EXTENSION_BADGE_COLORS[extension] ?? 'bg-gray-400',
      className
    )}
  >
    {extension}
  </span>
);

const Step = ({
  state,
  label,
  index,
}: {
  state: 'complete' | 'active' | 'inactive';
  label: string;
  index?: number;
}) => (
  <div className="flex items-start gap-3">
    {state === 'complete' ? (
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#292A33] text-white">
        <StepCheckIcon className="h-4 w-4" aria-hidden />
      </span>
    ) : (
      <span
        className={clsx(
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[17px] leading-none',
          state === 'active'
            ? 'bg-[#4B7EFF] text-white'
            : 'bg-[#EEEEF2] text-[#787887]'
        )}
      >
        {index}
      </span>
    )}
    <span
      className={clsx(
        'whitespace-nowrap text-[17px] leading-normal',
        state === 'complete' && 'text-[#292A33]',
        state === 'active' && 'text-[#4B7EFF]',
        state === 'inactive' && 'text-[#878787]'
      )}
    >
      {label}
    </span>
  </div>
);

const StepLine = () => <span className="h-px w-8 shrink-0 bg-[#BFBFBF]" />;

const DocumentCard = ({
  documentName,
  documentExtension,
  previewFile,
}: {
  documentName: string;
  documentExtension: string;
  previewFile: File | null;
}) => {
  const { base, suffix } = splitFileName(documentName, documentExtension);

  return (
    <div
      className={clsx(
        'flex w-full flex-col items-center gap-4 rounded-[20px] border border-[#D9D9D9] bg-white p-1 lg:gap-2 lg:p-2',
        previewFile && 'pb-4 lg:pb-2'
      )}
    >
      <div className="flex w-full items-center justify-between rounded-[16px] bg-[#DAF6ED] px-4 py-2 lg:py-6">
        <span className="flex items-center gap-2.5">
          <DocumentReadyIcon className="h-6 w-6 shrink-0" aria-hidden />
          <span className="whitespace-nowrap text-[16px] leading-5 text-[#292A33]">
            Your document is ready!
          </span>
        </span>
        <ExtensionBadge
          extension={documentExtension}
          className="hidden lg:flex"
        />
      </div>

      {previewFile && (
        <>
          <div className="flex w-full items-center justify-center gap-4 px-8 lg:hidden">
            <div className="relative flex h-[136px] shrink-0 items-start">
              <div className="h-[135.663px] w-24 overflow-hidden rounded-[4px] bg-[#EBEBEB] shadow-[0px_4px_12px_0px_rgba(0,0,0,0.16)]">
                <PaywallPdfPage
                  file={previewFile}
                  width={96}
                  fallback={<span className="block h-full w-full" />}
                />
              </div>
              <ExtensionBadge
                extension={documentExtension}
                className="pointer-events-none absolute -left-4 top-4"
              />
            </div>
            <p
              title={documentName}
              className="flex min-w-0 text-[14px] leading-normal text-[#787887]"
            >
              <span className="min-w-0 truncate">{base}</span>
              <span className="shrink-0">{suffix}</span>
            </p>
          </div>

          <div className="hidden max-h-[470px] w-full justify-center overflow-hidden rounded-[16px] bg-[#EEEEF2] lg:flex">
            <div className="my-9 w-[380px] shrink-0 self-start overflow-hidden rounded-[16px] bg-[#D9D9D9]">
              <PaywallPdfPage
                file={previewFile}
                width={380}
                fallback={<span className="block h-[537px] w-[380px]" />}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const FullAccessSection = ({
  accessTitle,
  hasTrial,
  trialDurationText,
  dueTodayAmountText,
  recurringAmountText,
  recurringPeriodText,
  showTopDivider,
  className,
}: {
  accessTitle: string;
  hasTrial: boolean;
  trialDurationText: string;
  dueTodayAmountText: string;
  recurringAmountText: string;
  recurringPeriodText: string;
  showTopDivider: boolean;
  className: string;
}) => (
  <div className={clsx('w-full flex-col gap-4 lg:gap-6', className)}>
    {showTopDivider && <span className="h-px w-full shrink-0 bg-[#EBEBEB]" />}
    <p className="text-[18px] font-bold uppercase leading-normal text-[#292A33]">
      {accessTitle}
    </p>
    <ul className="flex w-full flex-col gap-2.5">
      {PAYWALL_PLAN_FEATURES.map((feature) => (
        <li className="flex w-full items-center gap-2" key={feature}>
          <CircleCheckIcon
            className="h-6 w-6 shrink-0 text-[#016FD0]"
            aria-hidden
          />
          <span className="min-w-0 flex-1 break-words text-[16px] font-medium leading-normal text-[#292A33] lg:text-[18px]">
            {feature}
          </span>
        </li>
      ))}
    </ul>
    <span className="h-px w-full shrink-0 bg-[#EBEBEB]" />
    <Paywall2LegalText
      hasTrial={hasTrial}
      trialDurationText={trialDurationText}
      dueTodayAmountText={dueTodayAmountText}
      recurringAmountText={recurringAmountText}
      recurringPeriodText={recurringPeriodText}
    />
  </div>
);

const FullScreenPaywall2Layout = ({
  isOnlyPayment,
  hasTrial,
  trialDurationText,
  dueTodayAmountText,
  recurringAmountText,
  recurringPeriodText,
  documentName,
  documentExtension,
  documentFile,
  checkoutMethod,
  checkoutContainerRef,
  paymentStatus,
}: PaywallDocumentProps &
  PaywallCheckoutProps & {
    isOnlyPayment: boolean;
    hasTrial: boolean;
    trialDurationText: string;
    dueTodayAmountText: string;
    recurringAmountText: string;
    recurringPeriodText: string;
  }) => {
  const previewFile =
    !isOnlyPayment && documentFile?.type === 'application/pdf'
      ? documentFile
      : null;
  const accessProps = {
    accessTitle:
      hasTrial && trialDurationText
        ? `${trialDurationText} full access`
        : 'Full access',
    hasTrial,
    trialDurationText,
    dueTodayAmountText,
    recurringAmountText,
    recurringPeriodText,
  };

  return (
    <div className="flex w-full shrink-0 grow flex-col bg-[#F6F7F8] font-barlow text-[#292A33]">
      <div className="w-full px-4 lg:px-8">
        <div className="mx-auto flex w-full max-w-[1140px] items-center justify-between py-5">
          <img
            src="/images/logo/brand.svg"
            alt="PDF Genius"
            className="h-8 w-[136px] shrink-0"
          />
          {!isOnlyPayment && (
            <div className="hidden items-center gap-3 lg:flex">
              <Step state="complete" label="File is ready" />
              <StepLine />
              <Step state="active" index={2} label="Payment details" />
              <StepLine />
              <Step state="inactive" index={3} label="Download" />
            </div>
          )}
        </div>
      </div>

      <div className="w-full px-4 pb-4 lg:px-8 lg:pb-8">
        <div className="mx-auto flex w-full max-w-[1056px] flex-col gap-4 lg:flex-row lg:items-start">
          <section className="order-2 flex w-full min-w-0 flex-col rounded-[12px] bg-white p-4 lg:order-1 lg:flex-[570] lg:px-8 lg:py-6">
            <div className="flex w-full items-center justify-between gap-4 lg:whitespace-nowrap lg:py-2">
              <span className="text-[20px] font-semibold leading-normal text-[#292A33]">
                Total due today:
              </span>
              <PriceText value={dueTodayAmountText} />
            </div>

            {checkoutMethod && (
              <div className="relative w-full">
                <PaywallCheckoutFrame
                  checkoutContainerRef={checkoutContainerRef}
                  paymentStatus={paymentStatus}
                />
              </div>
            )}

            <FullAccessSection
              {...accessProps}
              showTopDivider
              className={clsx('flex pt-4 lg:pt-6', !previewFile && 'lg:hidden')}
            />
          </section>

          <aside className="order-1 flex w-full min-w-0 flex-col gap-4 lg:order-2 lg:flex-[470]">
            {!isOnlyPayment && (
              <DocumentCard
                documentName={documentName}
                documentExtension={documentExtension}
                previewFile={previewFile}
              />
            )}
            <div className="flex w-full items-center justify-between gap-4 overflow-hidden rounded-[20px] bg-[#DBE7FF] px-4 py-6 lg:whitespace-nowrap lg:p-6">
              <span className="text-[16px] font-medium leading-normal text-[#292A33] lg:text-[25px]">
                Total due today:
              </span>
              <PriceText value={dueTodayAmountText} />
            </div>
            {!previewFile && (
              <FullAccessSection
                {...accessProps}
                showTopDivider={false}
                className="hidden rounded-[12px] bg-white px-8 py-6 lg:flex"
              />
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

export default FullScreenPaywall2Layout;
